if (!customElements.get("main-product-v4")) {
  class MainProductV4 extends HTMLElement {
    constructor() {
      super();
      this.product = null;
      this.selectedVariant = null;

      this.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
      this.language = (window.Shopify && window.Shopify.locale) || "en-US";
      this.moneyFormatter = new Intl.NumberFormat(this.language, {
        style: "currency",
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onOtpToggle = this.onOtpToggle.bind(this);
      this.onFormClick = this.onFormClick.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onThumbClick = this.onThumbClick.bind(this);
      this.onInfoToggle = this.onInfoToggle.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      const jsonEl = this.querySelector(".main-product-json");
      if (!jsonEl) return;
      this.product = JSON.parse(jsonEl.innerHTML);

      this.optionInputs = Array.from(this.querySelectorAll(".main-product-v4__option-input"));
      this.offers = Array.from(this.querySelectorAll(".main-product-v4__offer"));
      this.variantIdInput = this.querySelector(".main-product-v4__variant-id");
      this.sellingPlanInput = this.querySelector(".main-product-v4__selling-plan-id");
      this.atcButton = this.querySelector(".main-product-v4__atc");
      this.atcLabel = this.querySelector(".main-product-v4__atc-label");
      this.atcPrice = this.querySelector("[data-atc-price]");
      this.atcCompare = this.querySelector("[data-atc-compare]");
      this.otpToggle = this.querySelector("[data-otp-toggle]");
      this.form = this.querySelector('form[action*="/cart/add"]');
      this._isRedirecting = false;

      // Per-variant content map (ingredients list, USP headline/description),
      // rendered by the section from variant richtext metafields.
      this.variantContentMap = null;
      this.variantContentDefaults = {};
      const contentMapEl = this.querySelector("[data-variant-content-map]");
      if (contentMapEl) {
        try {
          this.variantContentMap = JSON.parse(contentMapEl.textContent);
        } catch (err) {
          console.warn("main-product-v4: invalid variant content map:", err);
        }
      }

      // Per-variant gallery map (variant id -> [{src, thumb}], plus a
      // "default" entry) from the custom.custom_images variant metafields.
      // data-rendered-for says which entry the server already rendered.
      this.variantGalleryMap = null;
      this._gallerySwappedFor = "default";
      const galleryMapEl = this.querySelector("[data-variant-gallery-map]");
      if (galleryMapEl) {
        try {
          this.variantGalleryMap = JSON.parse(galleryMapEl.textContent);
          this._gallerySwappedFor = galleryMapEl.dataset.renderedFor || "default";
        } catch (err) {
          console.warn("main-product-v4: invalid variant gallery map:", err);
        }
      }

      // Sliders are presentational — never let them break purchasing.
      try {
        this.initSliders();
      } catch (err) {
        console.error("main-product-v4 slider init failed:", err);
      }
      this.initSelectedVariant();
      // Social-proof counter is decorative — never let it break purchasing.
      try {
        this.initViewingCount();
      } catch (err) {
        console.error("main-product-v4 viewing count failed:", err);
      }
      try {
        this.initBvReviewsLabel();
      } catch (err) {
        console.error("main-product-v4 BV reviews label failed:", err);
      }

      this.optionInputs.forEach((input) => input.addEventListener("change", this.onOptionChange));
      this.addEventListener("click", this.onThumbClick);
      if (this.otpToggle) this.otpToggle.addEventListener("click", this.onOtpToggle);
      // Capture-phase click interception, same pattern as the ab-tests
      // addToCartWithoutRedirect(): nothing else gets a chance to submit
      // the form or redirect.
      if (this.atcButton) this.atcButton.addEventListener("click", this.onFormClick, true);
      if (this.form) this.form.addEventListener("submit", this.onFormSubmit);
      // Delivery info tooltip(s) inside the selected offer card.
      if (this.querySelector("[data-info-toggle]")) {
        this.addEventListener("click", this.onInfoToggle);
        document.addEventListener("click", this.onDocumentClick);
        document.addEventListener("keydown", this.onKeydown);
      }
    }

    disconnectedCallback() {
      this.optionInputs.forEach((input) => input.removeEventListener("change", this.onOptionChange));
      this.removeEventListener("click", this.onThumbClick);
      if (this.otpToggle) this.otpToggle.removeEventListener("click", this.onOtpToggle);
      if (this.atcButton) this.atcButton.removeEventListener("click", this.onFormClick, true);
      if (this.form) this.form.removeEventListener("submit", this.onFormSubmit);
      this.removeEventListener("click", this.onInfoToggle);
      document.removeEventListener("click", this.onDocumentClick);
      document.removeEventListener("keydown", this.onKeydown);
      if (this.bvObserver) this.bvObserver.disconnect();
      const gallery = this.querySelector(".main-product-v4__gallery");
      if (gallery && gallery.swiper) gallery.swiper.destroy(true, true);
      const cards = this.querySelector(".main-product-v4__cards");
      if (cards && cards.swiper) cards.swiper.destroy(true, true);
    }

    /* Always resolve the live Swiper instances from the DOM — stored references
       can go stale when the section markup is re-rendered (theme editor,
       dev hot reload). */
    get galleryScroller() {
      const gallery = this.querySelector(".main-product-v4__gallery");
      return (gallery && gallery.swiper) || this.gallerySwiper || null;
    }

    get cardsScroller() {
      const cards = this.querySelector(".main-product-v4__cards");
      return (cards && cards.swiper) || null;
    }

    /* ---------- init ---------- */

    initSliders() {
      if (typeof Swiper === "undefined") return;

      const nextButton = this.querySelector(".main-product-v4__cards-next");
      const prevButton = this.querySelector(".main-product-v4__cards-prev");
      const navigation = {};
      if (nextButton) navigation.nextEl = nextButton;
      if (prevButton) navigation.prevEl = prevButton;
      const hasThumbs = Boolean(this.querySelector("[data-gallery-index]"));

      const gallery = this.querySelector(".main-product-v4__gallery");
      if (gallery && gallery.querySelectorAll(".swiper-slide").length > 1) {
        const galleryParams = { slidesPerView: 1, grabCursor: true };
        // In thumbnail mode the strip's arrows drive the gallery;
        // the strip itself just follows via syncActiveThumb().
        if (hasThumbs && nextButton) galleryParams.navigation = navigation;
        this.gallerySwiper = gallery.swiper || new Swiper(gallery, galleryParams);
        this.gallerySwiper.on("slideChange", () => this.syncActiveThumb());
        this.gallerySwiper.on("slideChange", () => this.restartActiveSlideVideo());
      }

      const cards = this.querySelector(".main-product-v4__cards");
      if (cards && !cards.swiper) {
        // Thumbnails: fixed 5 per view. Info cards keep their CSS-driven
        // "auto" widths. Both arrows are static and always visible — Swiper's
        // own navigation handles the disabled states at the ends.
        const cardParams = hasThumbs
          ? { slidesPerView: 5, spaceBetween: 16 }
          : { slidesPerView: "auto", spaceBetween: 16 };
        if (!hasThumbs && nextButton) cardParams.navigation = navigation;
        new Swiper(cards, cardParams);
      }
    }

    /* Live "people viewing" count — same data source and rules as the
       social-proof badge in snippets/ab-tests.liquid: Acadia analytics
       page views for the current path over the last day. The element is
       server-rendered hidden with a [count] template and only revealed on
       a successful fetch; counts under 5 keep it hidden, display caps at
       300+. Static texts (no [count] template) never reach this code. */
    initViewingCount() {
      const el = this.querySelector("[data-viewing-template]");
      if (!el) return;

      const pagePath = window.location.pathname;
      if (!pagePath || pagePath === "/") return;

      const url =
        "https://splittesting.acadia.io/analytics/social-proof" +
        "?pid=332332205&metrics=screenPageViews" +
        "&url=" + encodeURIComponent(pagePath) +
        "&startDate=1daysAgo";

      fetch(url, { method: "GET", credentials: "omit" })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const raw = data && data.value && data.value[0] && data.value[0].screenPageViews;
          const count = parseInt(String(raw), 10);
          if (!Number.isFinite(count) || count < 5) return;

          const display = count > 300 ? "300+" : String(count);
          const label = el.querySelector("[data-viewing-label]");
          if (!label) return;
          label.textContent = el.dataset.viewingTemplate.replace("[count]", display);
          el.hidden = false;
        })
        .catch(() => {
          // Endpoint down or blocked — element simply stays hidden.
        });
    }

    /* Bazaarvoice renders the review count as "(73)" — reformat it to
       "73 reviews" (suffix from the section setting). BV hydrates async and
       can re-render, so a MutationObserver keeps the format applied; writes
       are guarded to avoid an observer feedback loop. */
    initBvReviewsLabel() {
      const host = this.querySelector('[data-bv-show="rating_summary"]');
      if (!host) return;

      const suffix = host.dataset.reviewsSuffix || "";
      const format = () => {
        /* Inline !important styles beat BV's injected CleanSlate CSS no
           matter who renders last — the stylesheet rules alone lose some
           of these battles. */
        host.querySelectorAll(".bv_button_review_or_question_container, .bv_war_button").forEach((btn) => {
          btn.style.setProperty("display", "none", "important");
        });

        const el = host.querySelector(".bv_numReviews_text");
        if (!el) return;
        el.style.setProperty("text-decoration", "underline", "important");
        el.style.setProperty("text-underline-position", "from-font", "important");
        if (!suffix) return;
        const match = el.textContent.match(/\d+/);
        if (!match) return;
        const label = match[0] + " " + suffix;
        if (el.textContent !== label) el.textContent = label;
      };

      this.bvObserver = new MutationObserver(format);
      this.bvObserver.observe(host, { childList: true, subtree: true, characterData: true });
      format();
    }

    initSelectedVariant() {
      const urlVariantId = new URL(window.location).searchParams.get("variant");
      let variant = urlVariantId
        ? this.product.variants.find((v) => v.id === +urlVariantId)
        : null;

      if (variant) {
        this.checkInputsForVariant(variant);
      } else {
        variant = this.resolveVariantFromInputs();
      }
      if (!variant) {
        variant =
          this.product.variants.find((v) => v.metafields?.custom?.is_most_popular) ||
          this.product.variants.find((v) => v.available) ||
          this.product.variants[0];
        if (variant) this.checkInputsForVariant(variant);
      }

      this.applyVariant(variant, { updateUrl: Boolean(urlVariantId) });
    }

    checkInputsForVariant(variant) {
      if (!variant) return;
      this.optionInputs.forEach((input) => {
        input.checked = variant.options.includes(input.value);
      });
    }

    /* ---------- state changes ---------- */

    onOptionChange() {
      const variant = this.resolveVariantFromInputs();
      if (variant) this.applyVariant(variant, { updateUrl: true });
    }

    /* "one time purchase" is a direct buy action, not a mode switch: it adds
       the current variant to the cart WITHOUT a selling plan. Subscription
       stays the default for the main ATC button. */
    onOtpToggle(e) {
      e.preventDefault();
      const variant = this.selectedVariant;
      if (!variant) return;

      let variantId = variant.id;
      const offer = this.offerForValue(this.packValueOf(variant));
      const otpId = offer && offer.dataset.otpVariantId;
      if (otpId) variantId = +otpId;

      this.addItemsAndOpenCart([{ id: +variantId, quantity: 1 }]);
    }

    resolveVariantFromInputs() {
      const selected = this.optionInputs.filter((i) => i.checked).map((i) => i.value);
      return this.product.variants.find((variant) =>
        variant.options.every((opt) => selected.includes(opt))
      );
    }

    applyVariant(variant, { updateUrl } = {}) {
      if (!variant) return;
      this.selectedVariant = variant;
      this.setAttribute("data-variant", variant.id);

      if (updateUrl) {
        const url = new URL(window.location);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState(null, "", url);
      }

      this.syncFormInputs(variant);
      this.updateOfferPrices();
      this.updateAtc(variant);
      this.updateOtpToggle(variant);
      this.updateVariantMatchedElements(variant);
      this.updateVariantContent(variant);
      this.updateVariantGallery(variant);
      this.updateSelectedVariantLabels(variant);
      this.updateDiscountLabels(variant);
      this.slideGalleryToVariant(variant);

      // Other sections (e.g. the Ingredients v2 cards) swap their own
      // per-variant content off this event.
      document.dispatchEvent(
        new CustomEvent("mp4:variant-change", {
          detail: {
            variantId: variant.id,
            productId: this.product && this.product.id,
            variantContentAllowed: this.dataset.variantContentAllowed === "true",
          },
        })
      );
    }

    /* [data-selected-variant] spans come from the "{selected_variant}"
       placeholder in the subscription box benefits — keep them showing the
       selected pack value (e.g. "30 Bottles"). */
    updateSelectedVariantLabels(variant) {
      const value = this.packValueOf(variant);
      if (!value) return;
      this.querySelectorAll("[data-selected-variant]").forEach((el) => {
        if (el.textContent !== value) el.textContent = value;
      });
    }

    /* [data-first-order-discount] / [data-forever-discount] spans come from
       the "{first_order_discount}" / "{forever_discount}" placeholders in
       the subscription box benefits — keep them showing the selected
       variant's % off its compare-at price ("was" price = 100%) for the
       first order vs. every order after, mirroring the price_adjustments
       math used server-side (position 1 = first order, position 2+ =
       recurring; plans with only one adjustment reuse it for both). Falls
       back to variant.price when there's no compare-at price set. */
    updateDiscountLabels(variant) {
      const plan = variant.selling_plans && variant.selling_plans[0];
      const basePrice = +variant.compare_at_price || +variant.price || 0;
      if (!plan || basePrice <= 0) return;

      const adjustments = plan.price_adjustments || [];
      const firstOrderPrice = adjustments[0] ? +adjustments[0].price : +plan.price || 0;
      const foreverPrice = adjustments[1] ? +adjustments[1].price : firstOrderPrice;

      const firstOrderDiscount = Math.round(((basePrice - firstOrderPrice) * 100) / basePrice) + "%";
      const foreverDiscount = Math.round(((basePrice - foreverPrice) * 100) / basePrice) + "%";

      this.querySelectorAll("[data-first-order-discount]").forEach((el) => {
        if (el.textContent !== firstOrderDiscount) el.textContent = firstOrderDiscount;
      });
      this.querySelectorAll("[data-forever-discount]").forEach((el) => {
        if (el.textContent !== foreverDiscount) el.textContent = foreverDiscount;
      });
    }

    /* Swap the gallery (and thumbnail strip) to the selected variant's
       custom.custom_images. Variants without their own media get the map's
       "default" entry (product metafield, falling back to product images).
       Each entry is {type: "image"|"video", src, thumb} — custom_images is
       a file_reference metafield, so entries can be either. */
    updateVariantGallery(variant) {
      if (!this.variantGalleryMap) return;
      const hasOwn = Object.prototype.hasOwnProperty.call(this.variantGalleryMap, String(variant.id));
      const key = hasOwn ? String(variant.id) : "default";
      if (this._gallerySwappedFor === key) return;
      const entry = this.variantGalleryMap[key];
      if (!entry || entry.length === 0) return;

      const galleryWrapper = this.querySelector(".main-product-v4__gallery > .swiper-wrapper");
      if (!galleryWrapper) return;
      const thumbsWrapper = this.querySelector("[data-gallery-index]")
        ? this.querySelector(".main-product-v4__cards > .swiper-wrapper")
        : null;

      galleryWrapper.innerHTML = "";
      if (thumbsWrapper) thumbsWrapper.innerHTML = "";
      entry.forEach((image, index) => {
        const slide = document.createElement("div");
        slide.className = "main-product-v4__gallery-slide swiper-slide";

        let media;
        if (image.type === "video") {
          media = document.createElement("video");
          media.src = image.src;
          media.poster = image.thumb;
          media.autoplay = true;
          media.loop = true;
          media.muted = true;
          media.playsInline = true;
          media.controls = false;
        } else {
          media = document.createElement("img");
          media.src = image.src;
          media.alt = "";
          media.loading = index === 0 ? "eager" : "lazy";
        }
        slide.append(media);
        galleryWrapper.append(slide);

        if (thumbsWrapper) {
          const thumb = document.createElement("button");
          thumb.type = "button";
          thumb.className = "main-product-v4__card main-product-v4__card--thumb swiper-slide";
          if (index === 0) thumb.classList.add("is-active");
          thumb.dataset.galleryIndex = String(index);
          const thumbImg = document.createElement("img");
          thumbImg.src = image.thumb;
          thumbImg.alt = "";
          thumbImg.width = 143;
          thumbImg.height = 102;
          thumbImg.loading = "lazy";
          thumb.append(thumbImg);
          thumbsWrapper.append(thumb);
        }
      });

      this._gallerySwappedFor = key;

      const gallery = this.galleryScroller;
      if (gallery) {
        gallery.update();
        gallery.slideTo(0, 0);
      }
      const strip = this.cardsScroller;
      if (strip) {
        strip.update();
        strip.slideTo(0, 0);
      }
      this.syncActiveThumb();
    }

    /* Swap [data-variant-content] elements (ingredients list, USP headline/
       description) to the selected variant's richtext metafield HTML. The
       server-rendered markup is captured as the default on first pass and
       restored for variants without their own value. */
    updateVariantContent(variant) {
      if (!this.variantContentMap) return;
      const entry = this.variantContentMap[variant.id] || {};
      this.querySelectorAll("[data-variant-content]").forEach((el) => {
        const key = el.dataset.variantContent;
        if (!(key in this.variantContentDefaults)) {
          this.variantContentDefaults[key] = el.innerHTML;
        }
        const html = Object.prototype.hasOwnProperty.call(entry, key)
          ? entry[key]
          : this.variantContentDefaults[key];
        if (el.innerHTML !== html) {
          el.innerHTML = html;
          // Inside an open accordion the max-height was measured with the
          // old content — re-measure so nothing gets clipped.
          const accordionContent = el.closest(".accordion__content");
          if (accordionContent && accordionContent.style.maxHeight) {
            accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
          }
          // Group-list swaps replace whole accordion items — the fresh nodes
          // have no click listeners, so rebind them on the parent component.
          const accordions = el.closest("component-accordions");
          if (accordions && typeof accordions.accordions === "function") {
            accordions.accordions(".accordion__item", ".accordion__content", ".accordion__control", "open");
          }
        }
      });
    }

    syncFormInputs(variant) {
      if (this.sellingPlanInput) this.sellingPlanInput.value = this.sellingPlanIdFor(variant);
      if (this.variantIdInput) this.variantIdInput.value = variant.id;
    }

    sellingPlanIdFor(variant) {
      const plan = variant && variant.selling_plans && variant.selling_plans[0];
      return plan ? String(plan.id) : "";
    }

    subPriceFor(variant) {
      const plan = variant && variant.selling_plans && variant.selling_plans[0];
      return plan && plan.price != null ? plan.price : variant.price;
    }

    displayPriceFor(variant) {
      return this.subPriceFor(variant);
    }

    /* ---------- offer cards ---------- */

    packValueOf(variant) {
      const offerValues = this.offers.map((o) => o.dataset.offerValue);
      return variant.options.find((opt) => offerValues.includes(opt));
    }

    offerForValue(value) {
      return this.offers.find((o) => o.dataset.offerValue === value) || null;
    }

    variantForOffer(offerValue) {
      if (!this.selectedVariant) return null;
      const packValue = this.packValueOf(this.selectedVariant);
      const wanted = this.selectedVariant.options.map((opt) =>
        opt === packValue ? offerValue : opt
      );
      return this.product.variants.find((variant) =>
        variant.options.every((opt, i) => opt === wanted[i])
      );
    }

    updateOfferPrices() {
      this.offers.forEach((offer) => {
        const variant = this.variantForOffer(offer.dataset.offerValue);
        if (!variant) return;

        const price = this.displayPriceFor(variant);
        const compare = variant.compare_at_price || 0;

        const priceEl = offer.querySelector("[data-offer-price]");
        if (priceEl) priceEl.textContent = this.money(price);

        const compareEl = offer.querySelector("[data-offer-compare]");
        if (compareEl) {
          if (compare > price) {
            compareEl.textContent = this.money(compare);
            compareEl.style.display = "";
          } else {
            compareEl.style.display = "none";
          }
        }

        const pill = offer.querySelector("[data-offer-pill]");
        if (pill && !pill.hasAttribute("data-static-badge")) {
          // Selling plan price off a base price: the variant's compare-at
          // price when it's set and bigger than the selling plan price,
          // otherwise the variant's own one-time price.
          const variantCompare = +variant.compare_at_price || 0;
          const basePrice = variantCompare > price ? variantCompare : +variant.price || 0;
          if (basePrice > 0 && basePrice > price) {
            pill.textContent = Math.round(((basePrice - price) * 100) / basePrice) + "% off";
            pill.style.display = "";
          } else {
            pill.style.display = "none";
          }
        }

        const perUnit = offer.querySelector("[data-offer-per-unit]");
        if (perUnit) {
          const count = parseInt(offer.dataset.offerValue, 10);
          if (count > 0) {
            const suffix = perUnit.dataset.suffix || perUnit.textContent.replace(/^[^a-zA-Z]+/, "");
            perUnit.dataset.suffix = suffix;
            perUnit.textContent = this.money(Math.round(price / count)) + " " + suffix;
          }
        }
      });
    }

    updateAtc(variant) {
      if (!this.atcButton) return;
      this.atcButton.disabled = !variant.available;
      if (this.atcLabel && this.atcButton.dataset.textAdd) {
        this.atcLabel.textContent = variant.available
          ? this.atcButton.dataset.textAdd
          : this.atcButton.dataset.textSoldout;
      }
      if (this.atcPrice) this.atcPrice.textContent = this.money(this.displayPriceFor(variant));
      if (this.atcCompare) {
        const compare = this.compareFor(variant);
        if (compare > 0) {
          this.atcCompare.textContent = this.money(compare);
          this.atcCompare.style.display = "";
        } else {
          this.atcCompare.style.display = "none";
        }
      }
    }

    /* Compare-at price for the price actually displayed: the selling-plan
       allocation's compare price wins, then the variant compare price. */
    compareFor(variant) {
      const price = this.displayPriceFor(variant);
      const plan = variant.selling_plans && variant.selling_plans[0];
      const planCompare = plan ? +plan.compare_at_price || 0 : 0;
      if (planCompare > price) return planCompare;
      const variantCompare = +variant.compare_at_price || 0;
      if (variantCompare > price) return variantCompare;
      return 0;
    }

    /* ---------- delivery info tooltip ---------- */

    onInfoToggle(e) {
      const button = e.target.closest("[data-info-toggle]");
      if (!button) {
        this.closeTooltips();
        return;
      }
      e.preventDefault();
      const tooltip = document.getElementById(button.getAttribute("aria-controls"));
      if (!tooltip) return;
      const willOpen = tooltip.hidden;
      this.closeTooltips();
      if (willOpen) {
        tooltip.hidden = false;
        button.setAttribute("aria-expanded", "true");
      }
    }

    onDocumentClick(e) {
      if (!this.contains(e.target)) this.closeTooltips();
    }

    onKeydown(e) {
      if (e.key === "Escape") this.closeTooltips();
    }

    closeTooltips() {
      this.querySelectorAll(".main-product-v4__info-tooltip:not([hidden])").forEach((tooltip) => {
        tooltip.hidden = true;
      });
      this.querySelectorAll('[data-info-toggle][aria-expanded="true"]').forEach((button) => {
        button.setAttribute("aria-expanded", "false");
      });
    }

    updateOtpToggle(variant) {
      if (!this.otpToggle) return;
      const priceEl = this.otpToggle.querySelector("[data-otp-price]");
      if (priceEl) priceEl.textContent = this.money(variant.price);
      // Only makes sense to offer "buy once" when the default IS a subscription.
      const hasPlan = Boolean(this.sellingPlanIdFor(variant));
      this.otpToggle.style.display = hasPlan ? "" : "none";
    }

    updateVariantMatchedElements(variant) {
      const values = variant.options.map((opt) => opt.trim().toLowerCase());
      this.querySelectorAll("[data-variant-match]").forEach((el) => {
        const match = el.dataset.variantMatch.trim().toLowerCase();
        el.classList.toggle("mp4-variant-hidden", !values.includes(match));
      });
    }

    onThumbClick(e) {
      const thumb = e.target.closest("[data-gallery-index]");
      const gallery = this.galleryScroller;
      if (!thumb || !gallery) return;
      gallery.slideTo(+thumb.dataset.galleryIndex);
    }

    syncActiveThumb() {
      const gallery = this.galleryScroller;
      const index = gallery ? gallery.activeIndex : 0;
      this.querySelectorAll("[data-gallery-index]").forEach((thumb) => {
        thumb.classList.toggle("is-active", +thumb.dataset.galleryIndex === index);
      });
      const active = this.querySelector("[data-gallery-index].is-active");
      if (active) this.scrollThumbIntoView(active);

      // Clinicians' Choice sticker is pinned to the first image only —
      // fade it out as soon as the gallery moves past slide 1.
      const media = this.querySelector(".main-product-v4__media");
      if (media) media.classList.toggle("main-product-v4__media--past-first", index > 0);
    }

    /* The strip has fewer snap positions than slides (slidesPerView: "auto"),
       so slideTo(slideIndex) can be out of range — translate directly instead. */
    scrollThumbIntoView(thumb) {
      const strip = this.cardsScroller;
      if (!strip) return;
      const left = thumb.offsetLeft;
      const right = left + thumb.offsetWidth;
      const viewLeft = -strip.translate;
      const viewRight = viewLeft + strip.width;

      let target = strip.translate;
      if (right > viewRight) {
        target = -(right - strip.width);
      } else if (left < viewLeft) {
        target = -left;
      } else {
        return; // already fully visible
      }

      target = Math.max(strip.maxTranslate(), Math.min(strip.minTranslate(), target));
      strip.translateTo(target, 300);
    }

    slideGalleryToVariant(variant) {
      const gallery = this.galleryScroller;
      if (!gallery) return;
      const slides = Array.from(this.querySelectorAll(".main-product-v4__gallery-slide"));
      const index = slides.findIndex((slide) => {
        const ids = (slide.dataset.variant || "").split(",");
        return ids.includes(String(variant.id));
      });
      if (index >= 0) gallery.slideTo(index);
    }

    /* Video gallery slides autoplay/loop, but a slide that's been scrolled
       away and back should start over rather than resume mid-clip — restart
       whichever video is in the newly active slide on every slideChange. */
    restartActiveSlideVideo() {
      const gallery = this.galleryScroller;
      if (!gallery || !gallery.slides) return;
      const activeSlide = gallery.slides[gallery.activeIndex];
      const video = activeSlide && activeSlide.querySelector("video");
      if (!video) return;
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    }

    /* ---------- add to cart (ported from main-product-v2) ---------- */

    /* Same pattern as addToCartWithoutRedirect() in snippets/ab-tests.liquid:
       intercept the click in capture phase, add via /cart/add.js, then poll
       for the NSHC drawer and open it. Unlike v2, the checkout_link variant
       metafields are intentionally IGNORED here — v4 always keeps the user on
       the page and opens the drawer. */
    onFormClick(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.addToCartWithoutRedirect();
    }

    onFormSubmit(e) {
      // Safety net for programmatic submits — the click handler is the real path.
      e.preventDefault();
    }

    addToCartWithoutRedirect() {
      const form = this.form;
      if (!form) return;

      const formData = new FormData(form);
      const variantId = formData.get("id");
      const sellingPlan = formData.get("selling_plan");
      const quantity = formData.get("quantity") || 1;

      const items = [
        {
          id: parseInt(variantId),
          quantity: parseInt(quantity),
          ...(sellingPlan && { selling_plan: parseInt(sellingPlan) }),
        },
      ];

      (window.UPSELL_PRODUCTS || []).forEach((id) => {
        items.push({ id: +id, quantity: 1 });
      });

      if (sellingPlan && window.UPSELL_FOR_SUBSCRIPTION && window.UPSELL_FOR_SUBSCRIPTION[+variantId]) {
        window.UPSELL_FOR_SUBSCRIPTION[+variantId].forEach((id) => {
          items.push({ id: +id, quantity: 1 });
        });
      }

      this.addItemsAndOpenCart(items);
    }

    addItemsAndOpenCart(items) {
      if (!items.length) return;

      const first = items[0];
      let url = `/cart/add.js?items[0][id]=${first.id}&items[0][quantity]=${first.quantity}`;
      if (first.selling_plan) {
        url += `&items[0][selling_plan]=${first.selling_plan}`;
      }

      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items }),
        credentials: "include",
      })
        .then((response) => response.json())
        .then(() => {
          document.dispatchEvent(new CustomEvent("cart:update"));
          this.triggerCartOpen();
        })
        .catch((error) => console.error("PDP v4 add error:", error));
    }

    /* Poll until the NSHC drawer API and element are both ready, then open.
       Copied from ab-tests.liquid's triggerInitialCartOpen(). */
    triggerCartOpen() {
      const start = Date.now();
      const timeoutMs = 8000;

      const wait = setInterval(() => {
        const api = window.NSHybridCart;
        const hasApi = api && typeof api.open === "function";
        const hasElement = !!document.querySelector("ns-hybrid-cart");

        if (hasApi && hasElement) {
          clearInterval(wait);
          try {
            api.open();
          } catch (e) {
            // Don't brick the page if the cart isn't fully hydrated yet.
            setTimeout(() => {
              try {
                api.open();
              } catch (_) {}
            }, 250);
          } finally {
            window.nsHybridUpdated = true;
          }
        }

        if (Date.now() - start > timeoutMs) {
          clearInterval(wait);
        }
      }, 50);
    }

    money(price) {
      return this.moneyFormatter.format(price / 100.0).replace(/^A\$/, "$");
    }
  }

  customElements.define("main-product-v4", MainProductV4);
}
